import { invariant } from './invariant';

type KeywordMapping = Record<string, string | ((keyword: string) => string)>;

export const DEFAULT_ORIGINAL_FILENAME_TEMPLATE_KEY = 'originalFileName';
export const DEFAULT_ITEM_ID_TEMPLATE_KEY = 'itemId';

/**
 * This class is used to parse template strings that are in
 * the same style as used by the timed mdf system, such as:
 * "Hello { foo } { bar }",
 * and generate output strings by providing values for
 * the bracketed keywords.
 */
export class TemplateParser {

  private readonly template: string;
  private readonly parsed: ParsedTemplate = [];

  private currPos = 0; // Used to keep track of where we are in the input during parsing of the template

  constructor(template: string) {
    this.template = template;
    this.parse();
  }

  /**
   * Applies the given keyword mapping to the template,
   * returning the resulting string.
   * @param keywords The mapping between the template keyword and the value/function it should use to replace the keywords.
   * @param fallbackValue (optional) If provided, this value will be used instead of the raw template string as the fallback value
   * when a keyword has no mapping.
   */
  replace(keywords: KeywordMapping, fallbackValue?: string): string {
    return this.parsed
      .map(p => {
        if (typeof p === 'string') {
          return p;
        }
        // If we have mapping for the keyword we replace with the
        // corresponding value, otherwise we return the raw substring
        // of the input that contained the bracket pair.
        const mappedValue = keywords[p.keyword] ?? fallbackValue ?? p.raw;
        if (typeof mappedValue === 'string') return mappedValue;

        return mappedValue(p.keyword);
      })
      .join('');
  }

  private parse() {
    while (this.currentChar() !== null) {
      this.readToBracketStart();
      if (this.currentChar() === null) {
        break;
      }
      this.readReplacement();
    }
  }

  // Tries to parse a replacement such as "{ foo }".
  // If there is no closing bracket, the whole remaining substring
  // of the input is read and added raw.
  private readReplacement() {
    invariant(this.currentChar() === '{', 'expecting to start at a left bracket');
    const template = this.template;
    let pos = this.currPos + 1;
    while (pos < template.length) {
      if (template[pos] === '}') {
        this.parsed.push({
          keyword: template.substring(this.currPos + 1, pos).trim(),
          raw: template.substring(this.currPos, pos + 1)
        });
        break;
      }
      pos += 1;
    }

    if (template[pos] !== '}') {
      // There was no ending right bracket, so we just take the raw substring.
      this.parsed.push(template.substring(this.currPos, pos));
    }

    this.currPos = pos + 1;
  }

  // Parses the template until either we have a left bracket at currPos
  // or end of input, and adds the read string to the parse result.
  private readToBracketStart() {
    const template = this.template;
    let pos = this.currPos;
    while (pos < template.length && template[pos] !== '{') {
      pos += 1;
    }
    if (pos > this.currPos) {
      this.parsed.push(template.substring(this.currPos, pos));
      this.currPos = pos;
    }
  }

  private currentChar(): string | null {
    return this.currPos < this.template.length
      ? this.template[this.currPos]!
      : null;
  }
}


// Represents an input like 'hello world { someReplacement } test123,
// parsed into ['hello world ', Replacement, ' test123']
type ParsedTemplate = (string | Replacement)[];

type Replacement = {
  raw: string,
  keyword: string,
};
